import { useFormContext } from "react-hook-form";
import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { CompanyType } from "$types/CompanyType";
import useCompany from "$hooks/useCompany";
import { ShipmentService } from "$services";
import useAuth from "$hooks/useAuth";
import { getOps } from "$services/Op.service";

export default function useCompanyTypes({
  companyId,
  initialCompanyTypes,
  isReadOnly,
}: any) {
  const allCompanyTypes = Object.values(CompanyType);

  const TYPE_WARNING_MESSAGES = {
    lostSupplier:
      "L'entreprise va perdre son statut de fournisseur. Elle sera donc détachée des entreprises clientes auxquelles elle était attachée.",
    lostContributor:
      "L'entreprise va perdre son statut d'intervenant. Elle sera donc détachée des entreprises clients pour lesquelles elle intervenait, EDBs, chantiers et projets pour le(s)quel(s) elle intervenait.",
    lostClient:
      "L'entreprise va perdre son statut de client. Ses intervenants seront donc détachés d'elle, ses EDBs, chantiers et projets seront désactivés.",
  };

  const { token } = useAuth();
  const { watch } = useFormContext();
  const { isClient, isContributor, isSupplier } = useCompany();

  const companyTypes = watch("companyTypes");

  const [hasOneOpInActiveShipment, setHasOneOpInActiveShipment] =
    useState(false);

  const { data: ops, isLoading } = useQuery({
    queryKey: ["getOps", companyId],
    queryFn: () => getOps({ company: companyId }),
    gcTime: 60 * 1000,
    enabled: !!companyId,
  });

  const hadNoTypeInitially =
    !initialCompanyTypes || initialCompanyTypes?.length === 0;
  const hadOneTypeInitially = initialCompanyTypes?.length === 1;
  const hadTwoTypesInitially = initialCompanyTypes?.length === 2;

  const hasLostSupplier =
    isSupplier(initialCompanyTypes) && !isSupplier(companyTypes);
  const hasLostContributor =
    isContributor(initialCompanyTypes) && !isContributor(companyTypes);
  const hasLostClient =
    isClient(initialCompanyTypes) && !isClient(companyTypes);

  const disabledCompanyTypes = useMemo(() => {
    // Helper functions to make the logic more readable
    const removeTypes = (typesToRemove: CompanyType[]) =>
      allCompanyTypes.filter((ct) => !typesToRemove.includes(ct));

    const getDisabledTypesForClient = () => {
      if (hasOneOpInActiveShipment) {
        return removeTypes([CompanyType.CONTRIBUTOR]);
      }
      if (isContributor(companyTypes)) {
        return removeTypes([CompanyType.CONTRIBUTOR, CompanyType.CLIENT]);
      }
      if (isSupplier(companyTypes)) {
        return removeTypes([CompanyType.SUPPLIER, CompanyType.CLIENT]);
      }
      return removeTypes([
        CompanyType.SUPPLIER,
        CompanyType.CONTRIBUTOR,
        CompanyType.CLIENT,
      ]);
    };

    // read-only mode
    if (isReadOnly) {
      return allCompanyTypes;
    }

    if (hadNoTypeInitially) {
      // Single type selected that's not client/contributor/supplier
      if (
        companyTypes.length === 1 &&
        !isClient(companyTypes) &&
        !isContributor(companyTypes) &&
        !isSupplier(companyTypes)
      ) {
        return removeTypes([companyTypes[0]]);
      }

      if (companyTypes.length >= 1) {
        if (isContributor(companyTypes)) {
          return removeTypes([CompanyType.CONTRIBUTOR, CompanyType.CLIENT]);
        }
        if (isSupplier(companyTypes)) {
          return removeTypes([CompanyType.SUPPLIER, CompanyType.CLIENT]);
        }
        return removeTypes([
          CompanyType.SUPPLIER,
          CompanyType.CONTRIBUTOR,
          CompanyType.CLIENT,
        ]);
      }
    }

    // Handle case with one initial type
    if (hadOneTypeInitially) {
      if (isClient(initialCompanyTypes)) {
        return getDisabledTypesForClient();
      }
      if (isContributor(initialCompanyTypes)) {
        return removeTypes([CompanyType.CONTRIBUTOR, CompanyType.CLIENT]);
      }
      if (isSupplier(initialCompanyTypes)) {
        return removeTypes([CompanyType.SUPPLIER, CompanyType.CLIENT]);
      }

      return allCompanyTypes;
    }

    // Handle case with two initial types
    if (hadTwoTypesInitially && isClient(initialCompanyTypes)) {
      return getDisabledTypesForClient();
    }

    return [];
  }, [
    allCompanyTypes,
    hasOneOpInActiveShipment,
    companyTypes,
    hadNoTypeInitially,
    hadOneTypeInitially,
    hadTwoTypesInitially,
    initialCompanyTypes,
    isClient,
    isContributor,
    isSupplier,
    isReadOnly,
  ]);

  // Check if client has OP in a shipment
  useEffect(() => {
    const doGetHasOPInShipment = async () => {
      const hasOneOpInAShippedShipment =
        await ShipmentService.GetIsOneOpInAShippedShipment(
          token,
          ops?.data
            ?.filter((op) => op.company._id === companyId)
            ?.map((op) => op._id)
        );
      setHasOneOpInActiveShipment(hasOneOpInAShippedShipment);
    };
    if (companyId && isClient(companyTypes) && ops?.data?.length > 0) {
      doGetHasOPInShipment();
    }
  }, [companyId, companyTypes, isClient, ops, token]);

  return {
    ops,
    isLoading,
    hasOneOpInActiveShipment,
    setHasOneOpInActiveShipment,
    companyTypes,
    hadNoTypeInitially,
    hadOneTypeInitially,
    hadTwoTypesInitially,
    disabledCompanyTypes,
    hasLostSupplier,
    hasLostContributor,
    hasLostClient,
    TYPE_WARNING_MESSAGES,
    allCompanyTypes,
  };
}
